import { useState } from 'react'
import { Link as RouterLink, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  Alert, Box, Button, Chip, Link, Paper, Stack, Tab, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Tabs, TextField, Typography,
} from '@mui/material'
import { ETICHETTE_STATO, elencaImpiantiGreen, type ImpiantoGreen, type StatoFonte } from './green.api'
import { meseDaData } from './formato'
import { InArrivo } from '../layout/Pagina'

const configurato = (i: ImpiantoGreen) => i.statoDistributore === 'attivo' || i.statoGse === 'attivo'

export function ChipStato({ stato }: { stato: StatoFonte }) {
  const colore = stato === 'attivo' ? 'success' : stato === 'credenziali_mancanti' ? 'warning' : 'default'
  return <Chip size="small" color={colore} variant={stato === 'attivo' ? 'filled' : 'outlined'} label={ETICHETTE_STATO[stato]} />
}

export function Monitoraggio() {
  const { idPartner } = useParams()
  const [scheda, setScheda] = useState<'impianti' | 'non_configurati'>('impianti')
  const [cerca, setCerca] = useState('')
  const { data: impianti = [], error, isLoading } = useQuery({ queryKey: ['impianti-green'], queryFn: elencaImpiantiGreen })

  const testo = cerca.trim().toLowerCase()
  const righe = impianti
    .filter((i) => (scheda === 'impianti' ? configurato(i) : !configurato(i)))
    .filter((i) => testo === '' || `${i.ragioneSociale} ${i.codiceImpianto} ${i.comune}`.toLowerCase().includes(testo))
  const nonConfigurati = impianti.filter((i) => !configurato(i)).length

  return (
    <Stack spacing={2.5}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ justifyContent: 'space-between', alignItems: { sm: 'center' } }}>
        <Box>
          <Typography variant="h5" component="h1">Monitoraggio</Typography>
          <Typography color="text.secondary">Produzione, immissione e autoconsumo degli impianti, dai portali dei gestori.</Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <InArrivo etichetta="Esporta quarti orari" />
          <Button variant="contained" component={RouterLink} to={`/cliente/${idPartner}/green-configurazione`}>
            Aggiungi impianto
          </Button>
        </Stack>
      </Stack>

      {error && <Alert severity="error">{error.message}</Alert>}

      <Paper variant="outlined">
        <Stack direction={{ xs: 'column', md: 'row' }} sx={{ justifyContent: 'space-between', alignItems: { md: 'center' }, px: 2, pt: 1 }}>
          <Tabs value={scheda} onChange={(_e, v) => setScheda(v)}>
            <Tab value="impianti" label="Impianti" />
            <Tab value="non_configurati" label={`Non configurati (${nonConfigurati})`} />
          </Tabs>
          <TextField
            size="small"
            placeholder="Cerca impianto"
            value={cerca}
            onChange={(e) => setCerca(e.target.value)}
            slotProps={{ htmlInput: { 'aria-label': 'Cerca impianto' } }}
            sx={{ my: 1, minWidth: 240 }}
          />
        </Stack>
        <TableContainer>
          <Table size="small" aria-label="Impianti monitorati">
            <TableHead>
              <TableRow>
                <TableCell colSpan={4}>Anagrafica</TableCell>
                <TableCell colSpan={2}>Distributore</TableCell>
                <TableCell colSpan={2}>GSE</TableCell>
                <TableCell>Azioni</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Ragione sociale</TableCell>
                <TableCell>Codice impianto</TableCell>
                <TableCell align="right">kWp</TableCell>
                <TableCell>Comune</TableCell>
                <TableCell>Stato</TableCell>
                <TableCell>Ultimo mese</TableCell>
                <TableCell>Stato</TableCell>
                <TableCell>Ultimo scaricamento</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {righe.map((i) => (
                <TableRow key={i.id} hover>
                  <TableCell>{i.ragioneSociale}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{i.codiceImpianto}</TableCell>
                  <TableCell align="right">{i.kwp ?? '—'}</TableCell>
                  <TableCell>{i.comune}</TableCell>
                  <TableCell><ChipStato stato={i.statoDistributore} /></TableCell>
                  <TableCell>{meseDaData(i.ultimoMeseDistributore)}</TableCell>
                  <TableCell><ChipStato stato={i.statoGse} /></TableCell>
                  <TableCell>{i.ultimoScaricamentoGse ? new Date(i.ultimoScaricamentoGse).toLocaleDateString('it-IT') : '—'}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Link component={RouterLink} to={`/cliente/${idPartner}/impianti-green/${i.id}`}>Dettagli</Link>
                    <InArrivo etichetta="Report" />
                  </TableCell>
                </TableRow>
              ))}
              {!isLoading && righe.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9}>
                    <Typography color="text.secondary" sx={{ py: 2 }}>
                      {scheda === 'impianti' ? 'Nessun impianto con dati scaricati.' : 'Tutti gli impianti sono configurati.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Stack>
  )
}
